import React, { useState, useEffect } from 'react';
import { X, Printer, Download } from 'lucide-react';
import { Button } from './ui/button';
import { quotationsAPI, salesAPI, purchasesAPI } from '../services/part2-api';
import { toast } from 'react-hot-toast';

// Import all theme components
import ExecutiveGoldTheme from './print-themes/ExecutiveGoldTheme';
import ModernColorBlockTheme from './print-themes/ModernColorBlockTheme';
import MinimalProfessionalTheme from './print-themes/MinimalProfessionalTheme';
import RetailDenseTheme from './print-themes/RetailDenseTheme';
import ClassicElegantTheme from './print-themes/ClassicElegantTheme';
import CreativeModernTheme from './print-themes/CreativeModernTheme';

const THEME_COMPONENTS = {
  'executive_gold': ExecutiveGoldTheme,
  'modern_colorblock': ModernColorBlockTheme,
  'minimal_professional': MinimalProfessionalTheme,
  'retail_dense': RetailDenseTheme,
  'classic_elegant': ClassicElegantTheme,
  'creative_modern': CreativeModernTheme,
  // Legacy support with dashes (fallback)
  'executive-gold': ExecutiveGoldTheme,
  'modern-color-block': ModernColorBlockTheme,
  'minimal-professional': MinimalProfessionalTheme,
  'retail-dense': RetailDenseTheme,
  'classic-elegant': ClassicElegantTheme,
  'creative-modern': CreativeModernTheme
};

const PrintPreviewModal = ({ document, documentType = 'QUOTATION', documentId, onClose }) => {
  const [templateSettings, setTemplateSettings] = useState(null);
  const [fullDocument, setFullDocument] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [documentType, documentId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load template settings
      console.log('📋 Loading template settings for:', documentType.toLowerCase());
      const templateResponse = await quotationsAPI.getTemplateSettings(documentType.toLowerCase());
      console.log('📋 Template response:', templateResponse);
      console.log('📋 Template data:', templateResponse.data);
      console.log('📋 Selected theme:', templateResponse.data?.selected_theme);
      
      const settings = templateResponse.data || getDefaultSettings();
      console.log('📋 Final settings being used:', settings);
      setTemplateSettings(settings);
      
      // Load full document data if documentId is provided
      if (documentId) {
        console.log('📄 Loading document with ID:', documentId);
        let docResponse;
        if (documentType === 'QUOTATION') {
          docResponse = await quotationsAPI.get(documentId);
        } else if (documentType === 'SALES_INVOICE') {
          docResponse = await salesAPI.getInvoice(documentId);
        } else if (documentType === 'PURCHASE_INVOICE') {
          docResponse = await purchasesAPI.getInvoice(documentId);
        }
        console.log('📄 Document loaded:', docResponse?.data);
        setFullDocument(docResponse?.data || document);
      } else {
        setFullDocument(document);
      }
    } catch (error) {
      console.error('❌ Error loading data:', error);
      console.error('❌ Error details:', error.response?.data);
      toast.error('Failed to load print data');
      setTemplateSettings(getDefaultSettings());
      setFullDocument(document);
    } finally {
      setLoading(false);
    }
  };

  const getDefaultSettings = () => ({
    selected_theme: 'executive_gold',
    branding: { 
      company_name: 'Company Name',
      address: '123 Business Street, City, State - 123456',
      phone: '+91 12345 67890',
      email: 'contact@company.com',
      gstin: '29XXXXX1234X1ZX'
    },
    theme: { primary_color: '#6B7280', secondary_color: '#9CA3AF' },
    typography: { font_family: 'Inter, sans-serif' },
    toggles: {
      show_hsn: true,
      show_tax_breakup: true,
      show_signature: true,
      show_bank_details: false
    }
  });

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    toast.info('Tip: Use browser Print > Save as PDF for best results');
    setTimeout(() => {
      window.print();
    }, 500);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg p-8">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading template...</p>
        </div>
      </div>
    );
  }

  const selectedTheme = templateSettings?.selected_theme || 'executive_gold';
  console.log('🎨 Selected theme from settings:', selectedTheme);
  console.log('🎨 Available themes:', Object.keys(THEME_COMPONENTS));
  console.log('🎨 Template settings:', templateSettings);
  
  const ThemeComponent = THEME_COMPONENTS[selectedTheme] || ExecutiveGoldTheme;
  console.log('🎨 Theme component to use:', ThemeComponent?.name || 'ExecutiveGoldTheme (fallback)');
  
  // Prepare document for print with proper field mapping
  const printDocument = fullDocument ? {
    ...fullDocument,
    quotation_no: fullDocument.doc_no || fullDocument.quotation_no,
    doc_date: fullDocument.doc_date,
    party: fullDocument.party,
    items: fullDocument.items || [],
    totals: fullDocument.totals || {},
    tax_breakup: fullDocument.tax_breakup || [],
    terms: fullDocument.terms || {},
    meta: fullDocument.meta || {},
    payment: fullDocument.payment || null
  } : document;

  return (
    <>
      {/* Modal Overlay */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 print:hidden">
        <div className="h-full flex flex-col">
          {/* Header Bar */}
          <div className="bg-white border-b shadow-lg px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Print Preview</h2>
              <p className="text-sm text-gray-600 mt-1">
                Using <span className="font-semibold">{selectedTheme.replace(/_/g, ' ').replace(/-/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</span> theme
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={handleDownloadPDF}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
              <Button
                onClick={handlePrint}
                className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Close"
              >
                <X className="w-6 h-6 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Preview Area with Scrolling */}
          <div className="flex-1 overflow-auto bg-gray-100 p-8">
            <div className="max-w-[210mm] mx-auto bg-white shadow-2xl">
              <ThemeComponent document={printDocument} settings={templateSettings} />
            </div>
          </div>
        </div>
      </div>

      {/* Print-only content */}
      <div className="hidden print:block">
        <ThemeComponent document={printDocument} settings={templateSettings} />
      </div>
    </>
  );
};

export default PrintPreviewModal;
